import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {ApiService} from './api.service';
import {CommonUtils} from "../utils/common";

/**
 * Guard zarządzający kontrolą dostępu do modułu konfiguracji
 * gatunków zwierząt obsługiwanych przez klinikę (Vet Clinic Pet Species).
 * Weryfikuje po stronie serwera API, czy zalogowany użytkownik
 * ma odpowiednie uprawnienia administracyjne.
 */
@Injectable({
  providedIn: 'root'
})
export class VetClinicPetSpeciesAccessGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }

  /**
   * Ewaluuje uprawnienia przed asynchronicznym pobraniem (Lazy Loading) modułu konfiguracji gatunków zwierząt.
   * Minimalizuje obciążenie i zabezpiecza kod źródłowy poprzez weryfikację ról systemowych
   * na dedykowanym endpointcie API przed inicjalizacją transferu plików do przeglądarki.
   *
   * @param route Konfiguracja chronionej ścieżki.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku posiadania odpowiednich ról).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.apiService.post('/dashboard/vet-clinic-pet-species-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - vet-clinic-pet-species - ok');
      return true;
    }));
  }

  /**
   * Weryfikuje uprawnienia w momencie próby aktywacji interfejsu konfiguracji gatunków zwierząt.
   * Chroni spójność sesji aplikacji poprzez obsługę błędu HTTP 401 (wylogowanie)
   * oraz deleguje komunikację braku uprawnień biznesowych do ustandaryzowanej klasy CommonUtils.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub bezpieczne drzewo routingu (UrlTree).
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.apiService.post('/dashboard/vet-clinic-pet-species-access', {}).pipe(take(1), catchError(err => {
      if (err.status === 401) {
        this.router.navigate(['signin']).then(() => {
          this.authService.logout();
        });
      }
      return throwError(err);
    }), map(response => {
      console.log('check - canActivate - vet-clinic-pet-species - ok', response);

      return this.commonUtils.handleHttpResponseShowInfoToast(response, '', 'Nie masz uprawnień do tej strony')
    }));
  }

}
